import cx from "classnames";
import { useRef, useState } from "react";

import ActionListItem from "@/appComponents/Blocks/Composites/ActionListItem/ActionListItem";
import { AdditionalPhotoConfigModal } from "./AdditionalPhotoConfigModal";
import { Button, Flex, List } from "@theorgchartdev/storybook";
import { DataSourceButton, DataSourceMenu } from "../DataSourceButton";
import { ImportTypes } from "@/Utils/Constants";
import { DATA_SOURCES_TEST_PREFIX, SetLastValidationTime } from "../Utils";
import { appstr } from "@coreapp/utils/appstr";
import { kPhotoConnectorList } from "@coreapp/whitelabel/brand";

const BuildSourceOptions = (onClick, chartConfig) => {
  const options = [];
  for (const sourceObj of kPhotoConnectorList) {
    const newOption = {};
    newOption.content = (
      <DataSourceButton
        chartConfig={chartConfig}
        type={ImportTypes.photo}
        dataSource={sourceObj.command}
        removeBackground={true}
        textProps={{ size: "xsmall" }}
      />
    );

    newOption.value = sourceObj.command;
    newOption.clickHandler = () => onClick(sourceObj);
    options.push(newOption);
  }

  return options;
};

export const AdditionalPhotoConfigSection = props => {
  const source = props.chartConfig?.import?.photo?.photoSource ?? null;

  const menuOpenCtrlRef = useRef(null);
  const [workingConfig, setWorkingConfig] = useState(null);

  const handleShowModal = () => {
    const curConfig = props.chartConfig ?? {};
    setWorkingConfig(structuredClone(curConfig));
  };

  const handleCloseModal = () => {
    setWorkingConfig(null);
  };

  const handleSave = () => {
    props.handleUpdate({ import: workingConfig.import }, "import");
  };

  const handleChangeDataSource = ({ command }) => {
    menuOpenCtrlRef.current?.setOpen(false);

    // Prevent same data source selection from clearing the state
    if (command === source) {
      return;
    }

    const nextChartConfig = structuredClone(props.chartConfig);
    SetLastValidationTime(nextChartConfig, "photo", null);

    props.handleUpdate(
      {
        import: {
          ...nextChartConfig.import,
          photo: {
            photoSource: command
          }
        }
      },
      "import"
    );
  };

  const handleClear = () => {
    const nextChartConfig = structuredClone(props.chartConfig);
    SetLastValidationTime(nextChartConfig, "photo", null);

    props.handleUpdate(
      {
        import: {
          ...nextChartConfig.import,
          photo: undefined
        }
      },
      "import"
    );
  };

  return (
    <>
      <Flex direction={"column"} gap={"8px"} flex={"1"}>
        <div className={cx("oc-list-container", "ocn-list-scrollbar")}>
          <List
            enableVirtualization={false}
            listWidth="fill"
            options={[
              {
                value: "_",
                content: (
                  <ActionListItem
                    actions={[
                      {
                        disabled: !source,
                        iconName: "trash-can",
                        iconProps: { size: "medium", color: "error" },
                        onClick: handleClear,
                        dataTestId: `${DATA_SOURCES_TEST_PREFIX}-photo-source-clear`
                      }
                    ]}
                    buttonSize="xlarge"
                    contentBefore={
                      <DataSourceMenu
                        dataTestId={`${DATA_SOURCES_TEST_PREFIX}-photo-source-menu`}
                        chartConfig={props?.chartConfig}
                        openCtrlRef={menuOpenCtrlRef}
                        dataSource={source}
                        type={ImportTypes.photo}
                        options={BuildSourceOptions(handleChangeDataSource, props?.chartConfig)}
                        popoverProps={{
                          options: { placement: "bottom-start" }
                        }}
                        flexProps={{
                          direction: "row",
                          gap: "4px",
                          alignItems: "center"
                        }}
                        triggerProps={{
                          imageWidth: 48,
                          imageHeight: 48,
                          buttonWidth: 60,
                          removeBackground: true,
                          layout: "inline"
                        }}
                        labelAfter={true}
                        listProps={{ gridCols: 2 }}
                      />
                    }
                  />
                )
              }
            ]}
            coloredSelection={true}
          />
        </div>
        <Flex alignItems={"flex-start"} className={"oc-bottom-justify"}>
          <Button
            dataTestId={`${DATA_SOURCES_TEST_PREFIX}-photo-configure`}
            disabled={!source}
            isFullWidth={false}
            modifier={"secondary"}
            onClick={handleShowModal}
          >
            {appstr("Configure")}
          </Button>
        </Flex>
      </Flex>
      <AdditionalPhotoConfigModal
        show={workingConfig !== null}
        chartConfig={workingConfig}
        setChartConfig={setWorkingConfig}
        onClose={handleCloseModal}
        onSave={handleSave}
      />
    </>
  );
};
